import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { UpstreamMembersEditor } from '@/components/originGroups/UpstreamMembersEditor';
import { getApiErrorMessage } from '@/api/errors';
import { isValidOriginHost, isValidPort } from '@/utils/validation';
import type {
  OriginGroup,
  CreateOriginGroupRequest,
  UpdateOriginGroupRequest,
  UpstreamMember,
  HealthCheckConfig,
} from '@/types/originGroup';
import './OriginGroupModal.css';

/**
 * OriginGroupModal component props
 */
export interface OriginGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Create an origin group (create mode) */
  onCreate: (data: CreateOriginGroupRequest) => Promise<OriginGroup>;
  /** Update an origin group (edit mode) */
  onUpdate: (id: string, data: UpdateOriginGroupRequest) => Promise<OriginGroup>;
  originGroup?: OriginGroup | null;
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  address?: string;
  port?: string;
  members?: string;
}

/**
 * OriginGroupModal component for creating and editing origin groups
 */
export function OriginGroupModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  originGroup,
}: OriginGroupModalProps) {
  // Form state - Basic
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [port, setPort] = useState(443);
  const [enableTls, setEnableTls] = useState(true);
  const [verifyCertificate, setVerifyCertificate] = useState(true);
  const [certificateHostname, setCertificateHostname] = useState('');
  const [overrideHostHeader, setOverrideHostHeader] = useState('');

  // Form state - Timeouts
  const [connectTimeoutMs, setConnectTimeoutMs] = useState(5000);
  const [firstByteTimeoutMs, setFirstByteTimeoutMs] = useState(15000);
  const [betweenBytesTimeoutMs, setBetweenBytesTimeoutMs] = useState(10000);
  const [maxConnections, setMaxConnections] = useState(100);

  // Form state - Upstream members
  const [members, setMembers] = useState<UpstreamMember[]>([]);

  // Form state - Health check
  const [healthCheckEnabled, setHealthCheckEnabled] = useState(false);
  const [healthCheckPath, setHealthCheckPath] = useState('/');
  const [healthCheckInterval, setHealthCheckInterval] = useState(30000);

  // Form state - UI
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'members' | 'health'>('basic');

  const isEditMode = !!originGroup;

  // Reset form when modal opens/closes or originGroup changes
  useEffect(() => {
    if (isOpen) {
      if (originGroup) {
        setName(originGroup.name);
        setDescription(originGroup.description || '');
        setAddress(originGroup.address);
        setPort(originGroup.port);
        setEnableTls(originGroup.enable_tls);
        setVerifyCertificate(originGroup.verify_certificate);
        setCertificateHostname(originGroup.certificate_hostname || '');
        setOverrideHostHeader(originGroup.override_host_header || '');
        setConnectTimeoutMs(originGroup.connect_timeout_ms);
        setFirstByteTimeoutMs(originGroup.first_byte_timeout_ms);
        setBetweenBytesTimeoutMs(originGroup.between_bytes_timeout_ms);
        setMaxConnections(originGroup.max_connections);
        setMembers(originGroup.members || []);
        if (originGroup.health_check) {
          setHealthCheckEnabled(originGroup.health_check.enabled);
          setHealthCheckPath(originGroup.health_check.path || '/');
          setHealthCheckInterval(originGroup.health_check.interval_ms || 30000);
        } else {
          setHealthCheckEnabled(false);
          setHealthCheckPath('/');
          setHealthCheckInterval(30000);
        }
      } else {
        // Reset to defaults for new origin group
        setName('');
        setDescription('');
        setAddress('');
        setPort(443);
        setEnableTls(true);
        setVerifyCertificate(true);
        setCertificateHostname('');
        setOverrideHostHeader('');
        setConnectTimeoutMs(5000);
        setFirstByteTimeoutMs(15000);
        setBetweenBytesTimeoutMs(10000);
        setMaxConnections(100);
        setMembers([]);
        setHealthCheckEnabled(false);
        setHealthCheckPath('/');
        setHealthCheckInterval(30000);
      }
      setErrors({});
      setSubmitError(null);
      setActiveTab('basic');
    }
  }, [isOpen, originGroup]);

  // Update default port when TLS is toggled
  useEffect(() => {
    if (!isEditMode) {
      setPort(enableTls ? 443 : 80);
    }
  }, [enableTls, isEditMode]);

  /**
   * Validate the form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    // Address validation
    if (!address.trim()) {
      newErrors.address = 'Address is required';
    } else if (!isValidOriginHost(address.trim())) {
      newErrors.address = 'Invalid hostname or IPv4 address';
    }

    // Port validation
    if (!isValidPort(port)) {
      newErrors.port = 'Port must be between 1 and 65535';
    }

    // Members validation
    if (members.length > 0) {
      for (const member of members) {
        if (!member.address || !isValidOriginHost(member.address.trim())) {
          newErrors.members = 'All upstream members must have a valid hostname or IPv4 address';
          break;
        }
        if (!isValidPort(member.port)) {
          newErrors.members = 'All upstream member ports must be between 1 and 65535';
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Build health check config
    let healthCheck: HealthCheckConfig | undefined;
    if (healthCheckEnabled) {
      healthCheck = {
        enabled: true,
        path: healthCheckPath,
        method: 'GET',
        expected_status: 200,
        interval_ms: healthCheckInterval,
        timeout_ms: 5000,
        unhealthy_threshold: 3,
        healthy_threshold: 2,
      };
    }

    // Build request data
    const data: CreateOriginGroupRequest = {
      name: name.trim(),
      description: description.trim() || null,
      address: address.trim(),
      port,
      enable_tls: enableTls,
      verify_certificate: verifyCertificate,
      certificate_hostname: certificateHostname.trim() || null,
      override_host_header: overrideHostHeader.trim() || null,
      connect_timeout_ms: connectTimeoutMs,
      first_byte_timeout_ms: firstByteTimeoutMs,
      between_bytes_timeout_ms: betweenBytesTimeoutMs,
      max_connections: maxConnections,
      health_check: healthCheck || null,
      members:
        members.length > 0
          ? members.map((member) => ({ ...member, address: member.address.trim() }))
          : undefined,
    };

    setIsSaving(true);
    try {
      if (originGroup) {
        // An empty array clears previously configured members
        await onUpdate(originGroup.id, { ...data, members: data.members ?? [] });
      } else {
        await onCreate(data);
      }
      onClose();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Failed to save origin group'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Origin Group' : 'Add Origin Group'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="origin-group-modal-form" noValidate>
        {/* Tabs */}
        <div className="origin-group-modal-tabs">
          <button
            type="button"
            className={`origin-group-modal-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic
          </button>
          <button
            type="button"
            className={`origin-group-modal-tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            Upstream Servers
            {members.length > 0 && <span className="tab-badge">{members.length}</span>}
          </button>
          <button
            type="button"
            className={`origin-group-modal-tab ${activeTab === 'health' ? 'active' : ''}`}
            onClick={() => setActiveTab('health')}
          >
            Health Check
          </button>
        </div>

        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div className="origin-group-modal-section">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., backend-api"
              error={errors.name}
              disabled={isEditMode}
              required
            />

            <Input
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />

            <div className="origin-group-modal-row">
              <Input
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g., api.example.com"
                error={errors.address}
                required
              />
              <Input
                label="Port"
                type="number"
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value) || 0)}
                error={errors.port}
                style={{ maxWidth: '100px' }}
              />
            </div>

            <div className="origin-group-modal-checkboxes">
              <label className="origin-group-modal-checkbox">
                <input
                  type="checkbox"
                  checked={enableTls}
                  onChange={(e) => setEnableTls(e.target.checked)}
                />
                <span>Enable TLS (HTTPS)</span>
              </label>

              {enableTls && (
                <label className="origin-group-modal-checkbox">
                  <input
                    type="checkbox"
                    checked={verifyCertificate}
                    onChange={(e) => setVerifyCertificate(e.target.checked)}
                  />
                  <span>Verify SSL Certificate</span>
                </label>
              )}
            </div>

            {enableTls && (
              <Input
                label="Certificate Hostname (SNI)"
                value={certificateHostname}
                onChange={(e) => setCertificateHostname(e.target.value)}
                placeholder="Optional: hostname for SNI"
              />
            )}

            <Input
              label="Override Host Header"
              value={overrideHostHeader}
              onChange={(e) => setOverrideHostHeader(e.target.value)}
              placeholder="Optional: override Host header"
            />

            <div className="origin-group-modal-subheading">Timeouts</div>

            <div className="origin-group-modal-row">
              <Input
                label="Connect (ms)"
                type="number"
                value={connectTimeoutMs}
                onChange={(e) => setConnectTimeoutMs(parseInt(e.target.value) || 0)}
              />
              <Input
                label="First Byte (ms)"
                type="number"
                value={firstByteTimeoutMs}
                onChange={(e) => setFirstByteTimeoutMs(parseInt(e.target.value) || 0)}
              />
              <Input
                label="Between Bytes (ms)"
                type="number"
                value={betweenBytesTimeoutMs}
                onChange={(e) => setBetweenBytesTimeoutMs(parseInt(e.target.value) || 0)}
              />
            </div>

            <Input
              label="Max Connections"
              type="number"
              value={maxConnections}
              onChange={(e) => setMaxConnections(parseInt(e.target.value) || 0)}
              style={{ maxWidth: '150px' }}
            />
          </div>
        )}

        {/* Upstream Members Tab */}
        {activeTab === 'members' && (
          <UpstreamMembersEditor
            members={members}
            onChange={setMembers}
            defaultPort={enableTls ? 443 : 80}
            error={errors.members}
          />
        )}

        {/* Health Check Tab */}
        {activeTab === 'health' && (
          <div className="origin-group-modal-section">
            <label className="origin-group-modal-checkbox">
              <input
                type="checkbox"
                checked={healthCheckEnabled}
                onChange={(e) => setHealthCheckEnabled(e.target.checked)}
              />
              <span>Enable Health Checks</span>
            </label>

            {healthCheckEnabled && (
              <>
                <Input
                  label="Health Check Path"
                  value={healthCheckPath}
                  onChange={(e) => setHealthCheckPath(e.target.value)}
                  placeholder="/health"
                />

                <Input
                  label="Check Interval (ms)"
                  type="number"
                  value={healthCheckInterval}
                  onChange={(e) => setHealthCheckInterval(parseInt(e.target.value) || 30000)}
                  style={{ maxWidth: '150px' }}
                />

                <p className="origin-group-modal-help">
                  Health checks will mark the origin as unhealthy after 3 consecutive failures
                  and healthy after 2 consecutive successes.
                </p>
              </>
            )}
          </div>
        )}

        {/* Submit Error */}
        {submitError && (
          <div className="origin-group-modal-error">{submitError}</div>
        )}

        {/* Actions */}
        <div className="origin-group-modal-actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            {isEditMode ? 'Save Changes' : 'Create Origin Group'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
